import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { Col, Form, InputGroup, Modal, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import PinCodeBoxes from "../Core/PinCodeBoxes";
import GradientButton from "../Core/GradientButton";
import { useSessionStore } from "../../Stores/SessionStore";

const LoginModal = observer(function LoginModal(props) {
	const store = useSessionStore();
	const navigate = useNavigate();
	const [step, setStep] = useState("phone");
	const [phoneNumber, setPhoneNumber] = useState("");
	const [pin, setPin] = useState("");
	const [otp, setOtp] = useState("");
	const [error, setError] = useState("");
	const [resendSeconds, setResendSeconds] = useState(60);

	useEffect(() => {
		if (props.show) {
			setStep("phone");
			setPhoneNumber("");
			setPin("");
			setOtp("");
			setError("");
			setResendSeconds(60);
		}
	}, [props.show]);

	useEffect(() => {
		if (step !== "otp" || resendSeconds <= 0) {
			return undefined;
		}

		const timer = setInterval(() => {
			setResendSeconds((previous) => Math.max(0, previous - 1));
		}, 1000);

		return () => clearInterval(timer);
	}, [step, resendSeconds]);

	function normalizePhoneNumber(input) {
		const digits = (input || "").replace(/\D/g, "");
		if (digits.length === 10 && digits.startsWith("0")) {
			return digits.slice(1);
		}
		return digits;
	}

	function canProceedWithPhone() {
		return /^(?:\d{9}|0\d{9})$/.test(phoneNumber);
	}

	async function handleNext() {
		setError("");
		if (!canProceedWithPhone()) {
			setError("Please enter a valid 9 or 10 digit phone number.");
			return;
		}

		const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber);
		const result = await store.CustomersService.InitiateLogin(normalizedPhoneNumber);
		if (!result?.Success) {
			setError("We could not continue your login. Please try again.");
			return;
		}

		setPhoneNumber(normalizedPhoneNumber);

		if (result.Data?.HasPin) {
			setStep("pin");
			return;
		}

		// InitiateLogin already sends the OTP for customers without a PIN (same as mobile).
		setStep("otp");
		setResendSeconds(60);
	}

	async function handlePinLogin() {
		setError("");
		const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber);
		if (pin.length !== 4) {
			setError("Please enter your 4-digit PIN.");
			return;
		}

		const result = await store.LoginWithPin(normalizedPhoneNumber, pin);
		if (!result?.Success) {
			setError("Invalid phone number or PIN.");
			return;
		}

		navigate("/Portal/Home");
	}

	async function handleOtpLogin() {
		setError("");
		const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber);
		if (otp.length !== 4) {
			setError("Please enter your 4-digit OTP.");
			return;
		}

		const result = await store.LoginWithOtp(normalizedPhoneNumber, otp);
		if (!result?.Success) {
			setError("Invalid phone number or OTP.");
			return;
		}

		navigate("/Portal/Home");
	}

	async function handleResendOtp() {
		setError("");
		const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber);
		const result = await store.CustomersService.RequestLoginOtp(normalizedPhoneNumber);
		if (result?.Success) {
			setResendSeconds(60);
		}
	}

	async function handleForgotPin() {
		setError("");
		const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber);
		await store.CustomersService.RequestLoginOtp(normalizedPhoneNumber);
		setOtp("");
		setStep("otp");
		setResendSeconds(60);
	}

	return (
		<Modal size="lg" show={props.show}>
			<Modal.Header>
				<h3>{step === "otp" ? "Check SMS for code" : "Login"}</h3>
			</Modal.Header>
			<Modal.Body>
				{step === "phone" && (
					<Form
						onSubmit={(event) => {
							event.preventDefault();
							if (!store.LoggingIn && canProceedWithPhone()) {
								handleNext();
							}
						}}
					>
						<p className="mb-2">Enter your phone number below:</p>
						<Form.Group as={Row} className="mb-3">
							<Form.Label column sm={4}>Phone Number</Form.Label>
							<Col sm={8}>
								<InputGroup>
									<InputGroup.Text style={{ borderEndEndRadius: "0px", borderStartEndRadius: "0px" }} className="border-right-0">+27</InputGroup.Text>
									<Form.Control
										value={phoneNumber}
										maxLength={10}
										inputMode="numeric"
										onChange={(event) => setPhoneNumber(event.target.value.replace(/\D/g, ""))}
										onKeyDown={(event) => {
											if (event.key === "Enter") {
												event.preventDefault();
												if (!store.LoggingIn && canProceedWithPhone()) {
													handleNext();
												}
											}
										}}
										disabled={store.LoggingIn}
									/>
								</InputGroup>
							</Col>
						</Form.Group>
					</Form>
				)}

				{step === "pin" && (
					<div className="px-2 py-2">
						<h5 className="text-center mb-4">Enter your PIN</h5>
						<PinCodeBoxes
							value={pin}
							onChange={setPin}
							disabled={store.LoggingIn}
							autoFocus
							onEnter={() => {
								if (!store.LoggingIn && pin.length === 4) {
									handlePinLogin();
								}
							}}
						/>
						<p className="text-center mt-3">Log in using the PIN you selected when creating your profile.</p>
						<div className="d-flex justify-content-center mt-2">
							<small onClick={handleForgotPin} className="text-primary" style={{ cursor: "pointer" }}>
								Forgot your PIN? Request an OTP
							</small>
						</div>
					</div>
				)}

				{step === "otp" && (
					<div className="px-2 py-2">
						<p className="text-center mb-4">Enter verification code sent to +27 {phoneNumber}</p>
						<PinCodeBoxes
							value={otp}
							onChange={setOtp}
							disabled={store.LoggingIn}
							onEnter={() => {
								if (!store.LoggingIn && otp.length === 4) {
									handleOtpLogin();
								}
							}}
						/>
						<div className="d-flex justify-content-center mt-4">
							<small
								className={resendSeconds > 0 ? "text-muted" : "text-primary"}
								onClick={resendSeconds > 0 ? undefined : handleResendOtp}
								style={{ cursor: resendSeconds > 0 ? "default" : "pointer" }}
							>
								Resend Code (Available in {resendSeconds} seconds)
							</small>
						</div>
					</div>
				)}

				{error && <small className="text-danger d-block mt-3">{error}</small>}
			</Modal.Body>
			<Modal.Footer className="d-flex flex-column gap-2">
				{step === "phone" && (
					<>
						<GradientButton
							title={store.LoggingIn ? "Please wait..." : "Next"}
							variant="greenYellow"
							disabled={store.LoggingIn || !canProceedWithPhone()}
							loading={store.LoggingIn}
							onClick={handleNext}
						/>
						<GradientButton title="Cancel" variant="yellowOrange" disabled={store.LoggingIn} onClick={props.onClose} />
					</>
				)}
				{step === "pin" && (
					<>
						<GradientButton
							title={store.LoggingIn ? "Please wait..." : "Continue"}
							variant="greenYellow"
							disabled={store.LoggingIn || pin.length !== 4}
							loading={store.LoggingIn}
							onClick={handlePinLogin}
						/>
						<GradientButton title="Back to Login" variant="outline" disabled={store.LoggingIn} onClick={() => setStep("phone")} />
					</>
				)}
				{step === "otp" && (
					<>
						<GradientButton
							title={store.LoggingIn ? "Please wait..." : "Continue"}
							variant="greenYellow"
							disabled={store.LoggingIn || otp.length !== 4}
							loading={store.LoggingIn}
							onClick={handleOtpLogin}
						/>
						<GradientButton title="Back to Login" variant="outline" disabled={store.LoggingIn} onClick={() => setStep("phone")} />
					</>
				)}
			</Modal.Footer>
		</Modal>
	);
});

export default LoginModal;